<template>
  <div class="pay-fee-config-discount-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('payFeeConfigDiscountManage.page.title')"
      :subtitle="feeName || $t('payFeeConfigDiscountManage.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-back" @click="goBack">
          {{ $t('common.back') }}
        </el-button>
        <el-button type="primary" icon="el-icon-plus" @click="openAddModal">
          {{ $t('common.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="fetchData">
        <template #left>
          <span class="ds-panel__title">{{ $t('payFeeConfigDiscountManage.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="payFeeConfigDiscounts" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('payFeeConfigDiscountManage.page.empty')" @retry="fetchData">
        <el-table-column prop="configDiscountId" :label="$t('payFeeConfigDiscountManage.discountId')" align="center"
          width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.configDiscountId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="feeName" :label="$t('payFeeConfigDiscountManage.feeName')" align="left" min-width="140"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.feeName || feeName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="discountName" :label="$t('payFeeConfigDiscountManage.discountName')" align="left"
          min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.discountName || '-' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('payFeeConfigDiscountManage.rule')" align="left" min-width="180">
          <template slot-scope="scope">
            <div v-for="(item, index) in scope.row.feeDiscountSpecs" :key="index" class="ds-spec-line">
              {{ item.specName }}：{{ item.specValue }}
            </div>
            <span v-if="!scope.row.feeDiscountSpecs || !scope.row.feeDiscountSpecs.length" class="ds-text-muted">-</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('payFeeConfigDiscountManage.discountType')" align="center" width="100">
          <template slot-scope="scope">
            <ds-status-tag :type="scope.row.discountType === '1001' ? 'success' : 'warning'"
              :label="scope.row.discountType === '1001' ? $t('payFeeConfigDiscountManage.discount') : $t('payFeeConfigDiscountManage.penalty')" />
          </template>
        </el-table-column>
        <el-table-column :label="$t('payFeeConfigDiscountManage.paymentPeriod')" align="center" width="200">
          <template slot-scope="scope">
            <div class="ds-time-cell">
              <span class="ds-num">{{ scope.row.startTime || '-' }}</span>
              <span class="ds-num">{{ scope.row.endTime || '-' }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="payMaxEndTime" :label="$t('payFeeConfigDiscountManage.discountEndTime')" align="center"
          width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.payMaxEndTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="110" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" class="ds-btn-danger" @click="openDeleteModal(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <div class="ds-tip-text">
            <div>{{ $t('payFeeConfigDiscountManage.paymentPeriodTip') }}</div>
            <div>{{ $t('payFeeConfigDiscountManage.discountEndTimeTip') }}</div>
            <div>{{ $t('payFeeConfigDiscountManage.currentYearTip1') }}</div>
            <div>{{ $t('payFeeConfigDiscountManage.currentYearTip2') }}</div>
          </div>
        </template>
      </ds-pagination-bar>
    </div>

    <add-pay-fee-config-discount ref="addModal" @success="fetchData" />
    <delete-pay-fee-config-discount ref="deleteModal" @success="fetchData" />
  </div>
</template>

<script>
import { getPayFeeConfigDiscountList } from '@/api/fee/payFeeConfigDiscountManageApi'
import AddPayFeeConfigDiscount from '@/components/fee/addPayFeeConfigDiscount'
import DeletePayFeeConfigDiscount from '@/components/fee/deletePayFeeConfigDiscount'
import { getCommunityId } from '@/api/community/communityApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'PayFeeConfigDiscountManageList',
  components: {
    AddPayFeeConfigDiscount,
    DeletePayFeeConfigDiscount,
    DsPageHeader,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      payFeeConfigDiscounts: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      configId: '',
      feeName: '',
      communityId: ''
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.configId = this.$route.query.configId
    this.feeName = this.$route.query.feeName
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page: this.page.current,
          row: this.page.size,
          configId: this.configId,
          communityId: this.communityId
        }
        const response = await getPayFeeConfigDiscountList(params)
        this.payFeeConfigDiscounts = Array.isArray(response.data) ? response.data : []
        this.page.total = response.total || 0
      } catch (error) {
        console.error('获取费用折扣数据失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('payFeeConfigDiscountManage.page.loadFailed')
        this.$message.error(this.$t('payFeeConfigDiscountManage.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleSizeChange(size) {
      this.page.size = size
      this.page.current = 1
      this.fetchData()
    },
    handleCurrentChange(current) {
      this.page.current = current
      this.fetchData()
    },
    goBack() {
      this.$router.go(-1)
    },
    openAddModal() {
      this.$refs.addModal.open(this.configId)
    },
    openDeleteModal(row) {
      this.$refs.deleteModal.open(row)
    }
  }
}
</script>

<style lang="scss" scoped>
.pay-fee-config-discount-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 规则明细行 */
.ds-spec-line {
  line-height: 1.6;
}

/* 起止时间上下两行 */
.ds-time-cell {
  display: flex;
  flex-direction: column;
  line-height: 1.6;

  .ds-num {
    font-size: var(--ds-font-size-xs);
  }
}

/* 辅助文本 */
.ds-text-muted {
  color: var(--ds-gray-500);
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
